import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { saveToStore, getFromStore, getOneFromStore, getLastSyncTime, setLastSyncTime } from "@/lib/offlineDb";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OfflineDataContext = createContext();

export const useOfflineData = () => useContext(OfflineDataContext);

export const OfflineDataProvider = ({ children }) => {
  const [protocols, setProtocols] = useState([]);
  const [categories, setCategories] = useState([]);
  const [drugs, setDrugs] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [symptoms, setSymptoms] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [dataReady, setDataReady] = useState(false);

  // Track online/offline status
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Load cached data from IndexedDB on mount
  useEffect(() => {
    const loadCached = async () => {
      try {
        const [cachedProtocols, cachedCategories, cachedDrugs, cachedProcedures, cachedSymptoms, cachedSync] = await Promise.all([
          getFromStore('protocols'),
          getFromStore('categories'),
          getFromStore('drugs'),
          getFromStore('procedures'),
          getFromStore('symptoms'),
          getLastSyncTime()
        ]);
        if (cachedProtocols.length) setProtocols(cachedProtocols);
        if (cachedCategories.length) setCategories(cachedCategories);
        if (cachedDrugs.length) setDrugs(cachedDrugs);
        if (cachedProcedures.length) setProcedures(cachedProcedures);
        if (cachedSymptoms.length) setSymptoms(cachedSymptoms);
        if (cachedSync) setLastSync(cachedSync);
        setDataReady(true);
      } catch {
        setDataReady(true);
      }
    };
    loadCached();
  }, []);

  // Sync all data from network and save to IndexedDB
  const syncAllData = useCallback(async (force = false) => {
    if (syncing && !force) return;
    setSyncing(true);
    try {
      // Fetch each endpoint independently so one failure doesn't block others
      const results = await Promise.allSettled([
        axios.get(`${API}/protocols`, { params: { _t: Date.now() } }),
        axios.get(`${API}/categories`, { params: { _t: Date.now() } }),
        axios.get(`${API}/drugs`, { params: { _t: Date.now() } }),
        axios.get(`${API}/procedures`, { params: { _t: Date.now() } }),
        axios.get(`${API}/symptoms`, { params: { _t: Date.now() } })
      ]);

      const pData = results[0].status === "fulfilled" ? results[0].value.data : [];
      const cData = results[1].status === "fulfilled" ? results[1].value.data : [];
      const dData = results[2].status === "fulfilled" ? results[2].value.data : [];
      const prData = results[3].status === "fulfilled" ? results[3].value.data : [];
      const sData = results[4].status === "fulfilled" ? results[4].value.data : [];

      // Update state immediately so UI renders
      if (pData.length) setProtocols(pData);
      if (cData.length) setCategories(cData);
      if (dData.length) setDrugs(dData);
      if (prData.length) setProcedures(prData);
      if (sData.length) setSymptoms(sData);

      // Persist to IndexedDB
      const stores = [];
      if (pData.length) stores.push(saveToStore('protocols', pData));
      if (cData.length) stores.push(saveToStore('categories', cData));
      if (dData.length) stores.push(saveToStore('drugs', dData));
      if (prData.length) stores.push(saveToStore('procedures', prData));
      if (sData.length) stores.push(saveToStore('symptoms', sData));
      stores.push(setLastSyncTime());
      await Promise.all(stores);

      const now = Date.now();
      setLastSync(now);

      // Pre-cache individual protocol details in background (non-destructive)
      precacheProtocolDetails(pData);

      // Pre-cache images via service worker
      precacheImages();

      setSyncing(false);
      return true;
    } catch (error) {
      console.error("[OfflineData] Sync failed:", error);
      setSyncing(false);
      return false;
    }
  }, [syncing]);

  // Pre-cache individual protocol details in batches (non-destructive)
  const precacheProtocolDetails = async (protocolList) => {
    try {
      const BATCH_SIZE = 10;
      const detailedMap = {};
      
      for (let i = 0; i < protocolList.length; i += BATCH_SIZE) {
        const chunk = protocolList.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(
          chunk.map(p => axios.get(`${API}/protocols/${p.id}`).then(res => res.data))
        );
        results.forEach((r) => {
          if (r.status === "fulfilled" && r.value?.id) {
            detailedMap[r.value.id] = r.value;
          }
        });
      }

      // Merge: use detailed version where available, keep summary for the rest
      const merged = protocolList.map(p => detailedMap[p.id] || p);
      if (merged.length > 0) {
        await saveToStore('protocols', merged);
        setProtocols(merged);
      }
    } catch {
      // Non-critical — existing protocol list remains intact
    }
  };

  // Ask service worker to pre-cache images
  const precacheImages = () => {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({
        type: 'PRECACHE_IMAGES'
      });
    }
  };

  // Get a single protocol (from cache or network)
  const getProtocol = useCallback(async (id) => {
    // Try from memory first (already enriched with detail data)
    const fromMemory = protocols.find(p => p.id === id);
    if (fromMemory && fromMemory.care_levels) return fromMemory;

    // Try IndexedDB
    try {
      const cached = await getOneFromStore('protocols', id);
      if (cached && cached.care_levels) return cached;
    } catch {}

    // Try network
    if (isOnline) {
      try {
        const res = await axios.get(`${API}/protocols/${id}`);
        return res.data;
      } catch {}
    }

    return fromMemory || null;
  }, [protocols, isOnline]);

  // Get a single procedure
  const getProcedure = useCallback(async (id) => {
    if (isOnline) {
      try {
        const res = await axios.get(`${API}/procedures/${id}`);
        return res.data;
      } catch {}
    }
    const fromMemory = procedures.find(p => p.id === id);
    return fromMemory || null;
  }, [procedures, isOnline]);

  // Get symptom protocols
  const getSymptomProtocols = useCallback(async (symptomId) => {
    if (isOnline) {
      try {
        const res = await axios.get(`${API}/symptoms/${symptomId}`);
        return res.data;
      } catch {}
    }
    // Offline fallback: find symptom from cached list
    const symptom = symptoms.find(s => s.id === symptomId);
    if (!symptom) return null;
    return { ...symptom, protocols: [] };
  }, [symptoms, isOnline]);

  // Get drug protocols
  const getDrugProtocols = useCallback(async (drugId) => {
    if (isOnline) {
      try {
        const res = await axios.get(`${API}/drugs/${drugId}/protocols`);
        return res.data;
      } catch {}
    }
    return { protocols: [] };
  }, [isOnline]);

  // Auto-sync on first load if online - always sync on mount to catch any DB changes
  useEffect(() => {
    if (dataReady && isOnline) {
      syncAllData();
    }
  }, [dataReady, isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <OfflineDataContext.Provider value={{
      protocols, categories, drugs, procedures, symptoms,
      lastSync, syncing, isOnline, dataReady,
      syncAllData, getProtocol, getProcedure, getSymptomProtocols, getDrugProtocols
    }}>
      {children}
    </OfflineDataContext.Provider>
  );
};
